// SitewidePromoBanner — a slim, dismissable bar that auto-renders ONLY
// when an admin has activated a promo with `show_banner: true` whose
// start/end window is currently open. Sits below the navbar.
//
// Designed to be self-mounting (drop into App.js once and forget).
// Session-dismissed banners stay hidden until the next visit so we never
// nag a returning client.

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Tag, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function timeLeft(endsAt) {
  if (!endsAt) return "";
  try {
    const end = new Date(endsAt);
    const ms = end - new Date();
    if (ms <= 0) return "";
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    if (days > 1) return `Ends in ${days} days`;
    if (days === 1) return `Ends tomorrow`;
    if (hours > 1) return `Ends in ${hours} hours`;
    return `Ends today`;
  } catch {
    return "";
  }
}

export default function SitewidePromoBanner() {
  // Don't compete with the homepage's rotating "Trending Now" marquee —
  // that already surfaces active promos as inline pills. Showing both
  // creates visual clutter in the hero area (real client report).
  // Also suppress on the unmanned kiosk page (/welcome) — clients
  // checking in shouldn't be distracted by a marketing banner.
  const location = useLocation();
  const SUPPRESSED_PATHS = ["/", "/welcome", "/checkin", "/signage"];
  const SUPPRESSED_PREFIXES = ["/staff", "/admin", "/consultation/", "/consent"];
  const suppressed = SUPPRESSED_PATHS.includes(location.pathname)
    || SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ids = sessionStorage.getItem("kl_promo_dismissed_set") || "";
      return new Set(ids.split(",").filter(Boolean));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/commerce/promos/active`);
        if (!cancelled) setBanners(r.data?.banners || []);
      } catch {
        /* offline — silently hide */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(
    () => banners.filter((b) => !dismissed.has(b.id)),
    [banners, dismissed],
  );

  if (visible.length === 0 || suppressed) return null;
  const b = visible[0]; // Show one at a time — least intrusive
  const dismiss = () => {
    const next = new Set([...dismissed, b.id]);
    setDismissed(next);
    try { sessionStorage.setItem("kl_promo_dismissed_set", Array.from(next).join(",")); } catch { /* noop */ }
  };
  const ends = timeLeft(b.ends_at);
  const discountLabel =
    b.percent_off ? `${b.percent_off}% OFF` :
    b.amount_off ? `$${b.amount_off} OFF` :
    "Limited offer";

  return (
    <div
      className="fixed left-3 sm:left-5 top-[290px] sm:top-[330px] max-w-[calc(100%-1.5rem)] sm:max-w-sm rounded-2xl text-white shadow-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${b.color} 0%, ${b.color}d9 100%)`,
        zIndex: 60,
      }}
      data-testid="sitewide-promo-banner"
    >
      <div className="px-4 py-3 flex items-center gap-3 text-sm">
        <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
          <Tag className="w-3 h-3" /> {discountLabel}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold leading-snug" data-testid="sitewide-promo-headline">
            {b.headline}
          </div>
          {b.code && (
            <div className="mt-1 inline-flex items-center gap-1 bg-white/25 backdrop-blur-sm rounded px-2 py-0.5 font-mono text-xs font-bold tracking-wider">
              Code: {b.code}
            </div>
          )}
          {ends && (
            <div className="text-[11px] opacity-90 mt-1 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {ends}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="p-2 -m-2 text-white/85 hover:text-white flex-shrink-0"
          aria-label="Dismiss promo banner"
          data-testid="sitewide-promo-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
